import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { FaTimes, FaUpload, FaLink, FaYoutube, FaFileArchive } from 'react-icons/fa';
import { toast } from 'react-toastify';
import BatchMaterialService from '../../../../services/batchMaterialService';

const UploadMaterialModal = ({ show, onHide, batchId, currentFolder, onUpload }) => {
  const [uploadType, setUploadType] = useState('video');
  const [materialTitle, setMaterialTitle] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [materialLink, setMaterialLink] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [loading, setLoading] = useState(false);

  const resetForm = () => {
    setUploadType('video');
    setMaterialTitle('');
    setSelectedFile(null);
    setMaterialLink('');
    setUploadProgress(0);
  };

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const handleSubmit = async () => {
    // For YouTube Link
    if (uploadType === 'link') {
      if (!materialTitle.trim()) {
        toast.error('Title is required');
        return;
      }
      if (!materialLink.trim()) {
        toast.error('URL is required');
        return;
      }
      if (!materialLink.match(/^https?:\/\//)) {
        toast.error('Please enter a valid URL starting with http:// or https://');
        return;
      }

      const formData = new FormData();
      formData.append('parent_id', currentFolder || '');
      formData.append('type', 'link');
      formData.append('title', materialTitle);
      formData.append('link_url', materialLink);

      setLoading(true);
      try {
        await BatchMaterialService.uploadFile(batchId, formData);
        toast.success('Link added successfully');
        resetForm();
        onUpload();
        onHide();
      } catch (error) {
        toast.error('Failed to add link');
        console.error(error);
      } finally {
        setLoading(false);
      }
      return;
    }

    // For File Upload
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }
    if (!materialTitle.trim()) {
      toast.error('Title is required');
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('parent_id', currentFolder || '');
    formData.append('type', uploadType);
    formData.append('title', materialTitle);

    try {
      setLoading(true);
      setUploadProgress(0);
      
      // Simulate progress
      const interval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(interval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      await BatchMaterialService.uploadFile(batchId, formData);
      clearInterval(interval);
      setUploadProgress(100);
      
      toast.success('File uploaded successfully');
      resetForm();
      onUpload();
      onHide();
    } catch (error) {
      toast.error('Failed to upload file');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getButtonText = () => {
    if (loading) {
      return uploadType === 'link' ? 'Adding Link...' : 'Uploading...';
    }
    return uploadType === 'link' ? 'Add Link' : 'Upload File';
  };

  // Get accept attribute based on file type
  const getAcceptAttribute = () => {
    switch (uploadType) {
      case 'video':
        return 'video/*';
      case 'pdf':
        return '.pdf';
      case 'zip':
        return '.zip,.rar,.7z';
      case 'note':
        return '.doc,.docx,.ppt,.pptx,.txt';
      default:
        return '';
    }
  };

  // Get help text based on file type
  const getHelpText = () => {
    switch (uploadType) {
      case 'video':
        return 'Supported: MP4, AVI, MKV, MOV, etc.';
      case 'pdf':
        return 'Supported: PDF files only';
      case 'zip':
        return 'Supported: ZIP, RAR, 7Z archive files (students can download)';
      case 'note':
        return 'Supported: DOC, DOCX, PPT, PPTX, TXT';
      default:
        return '';
    }
  };

  return (
    <Modal show={show} onHide={() => {
      resetForm();
      onHide();
    }} centered size="lg">
      <Modal.Header closeButton className="bg-success text-white">
        <Modal.Title>
          <FaUpload className="me-2" />
          Add Batch Materials
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {/* Material Type Selection */}
        <Form.Group className="mb-3">
          <Form.Label>Material Type</Form.Label>
          <div>
            <Form.Check
              inline
              type="radio"
              label="Video (Upload)"
              name="uploadType"
              value="video"
              checked={uploadType === 'video'}
              onChange={(e) => setUploadType(e.target.value)}
            />
            <Form.Check
              inline
              type="radio"
              label="PDF"
              name="uploadType"
              value="pdf"
              checked={uploadType === 'pdf'}
              onChange={(e) => setUploadType(e.target.value)}
            />
            <Form.Check
              inline
              type="radio"
              label="Notes"
              name="uploadType"
              value="note"
              checked={uploadType === 'note'}
              onChange={(e) => setUploadType(e.target.value)}
            />
            <Form.Check
              inline
              type="radio"
              label="ZIP File"
              name="uploadType"
              value="zip"
              checked={uploadType === 'zip'}
              onChange={(e) => setUploadType(e.target.value)}
            />
            <Form.Check
              inline
              type="radio"
              label="YouTube Link"
              name="uploadType"
              value="link"
              checked={uploadType === 'link'}
              onChange={(e) => setUploadType(e.target.value)}
            />
          </div>
        </Form.Group>

        {/* Title Field (Common for all types) */}
        <Form.Group className="mb-3">
          <Form.Label>Title *</Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter title for this material"
            value={materialTitle}
            onChange={(e) => setMaterialTitle(e.target.value)}
          />
          <Form.Text className="text-muted">
            Display name that students will see
          </Form.Text>
        </Form.Group>

        {/* Conditional Fields based on type */}
        {uploadType === 'link' ? (
          <Form.Group className="mb-3">
            <Form.Label>YouTube/Video URL *</Form.Label>
            <Form.Control
              type="url"
              placeholder="https://www.youtube.com/watch?v=..."
              value={materialLink}
              onChange={(e) => setMaterialLink(e.target.value)}
            />
            <Form.Text className="text-muted">
              Enter the full YouTube video URL
            </Form.Text>
          </Form.Group>
        ) : (
          <Form.Group className="mb-3">
            <Form.Label>Select File *</Form.Label>
            <Form.Control
              type="file"
              onChange={handleFileChange}
              accept={getAcceptAttribute()}
            />
            <Form.Text className="text-muted">
              {getHelpText()}
            </Form.Text>
          </Form.Group>
        )}

        {/* Upload Progress (only for file uploads) */}
        {uploadProgress > 0 && uploadType !== 'link' && (
          <div className="mb-3">
            <div className="progress">
              <div
                className="progress-bar progress-bar-striped progress-bar-animated"
                style={{ width: `${uploadProgress}%` }}
              >
                {uploadProgress}%
              </div>
            </div>
          </div>
        )}

        {/* Folder Info */}
        {currentFolder && (
          <Alert variant="info" className="mt-2">
            Adding to: <strong>Current Folder</strong>
          </Alert>
        )}
      </Modal.Body>
      
      <Modal.Footer>
        <Button variant="secondary" onClick={() => {
          resetForm();
          onHide();
        }} disabled={loading}>
          <FaTimes className="me-2" /> Cancel
        </Button>
        <Button
          variant="success"
          onClick={handleSubmit}
          disabled={
            loading || 
            (uploadType === 'link' 
              ? (!materialTitle || !materialLink)
              : (!selectedFile || !materialTitle)
            )
          }
        >
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              {getButtonText()}
            </>
          ) : (
            <>
              {uploadType === 'link' ? <FaLink className="me-2" /> : 
               uploadType === 'zip' ? <FaFileArchive className="me-2" /> : 
               <FaUpload className="me-2" />}
              {getButtonText()}
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UploadMaterialModal;